// Every word Astra Lite shows on the landing page (#2549, ADR 0023). The
// founder picked variant "A v2" of the 2026-09-30 mock round: Astra runs the
// free read, a typing loop plays the sample students, a mic sits in the box.
// These lines are the mock's copy.md, section "A v2", word for word; the
// Playwright spec and AstraLite.test.jsx read them from here.

// What the page shows until the server says otherwise (the server's own
// fallback, astra_lite.FREE_QUESTIONS_DEFAULT).
export const FREE_QUESTIONS_FALLBACK = 3;
// The counter shows only in the day's last five questions (founder, #2725):
// both the open chat and the picture flow read this one number.
export const COUNTER_FROM = 5;

// The totals of who asks, as both privacy notices say them (#2903). One
// phrase per audience label the server counts (astra_lite_analytics.LABEL_KEYS,
// built in astra_lite_audience.profile): audience, grade, language (the
// visitor's words, else the browser's language at picture time), the
// catalog major, country/state/metro as "rough area", and first_gen only
// when stated. Add a label there and AstraNoticeDrift.test.js fails until
// this says it.
export const WHO_ASKS = 'who asks (parent, student or counselor), grade, language, intended major, rough area and first-generation if you say so';

// Questions and answers are kept to check answer quality (#3063; founder
// "proceed", 2026-10-04; Privacy Policy section 6). Literally true against
// astra_texts.py: 90 days (TEXT_TTL_DAYS), scrubbed of the names, emails,
// phones and street addresses the patterns detect (not a guarantee, so the
// line says "we can detect" and asks for no name), never for ads; nothing
// for Do Not Track.
// AstraNoticeDrift.test.js pins the 90 to the server's constant.
export const QUESTIONS_KEPT = "Questions and answers are kept up to 90 days to check answer quality. We remove the names, emails, phone numbers and street addresses we can detect first; please don't include your name. Never used for ads.";

export const ASTRA_LITE = {
    // The heading is the landing page's (FREE_READ.heading); the line under it
    // is variant A's own.
    sub: "Tell Astra about yourself like you'd text a friend. She reads your grades and scores against any school's real admitted class, then answers your questions. Free, before you sign in.",
    panelName: 'Astra',
    panelLine: 'Free read, no sign-in',
    greeting: "Hi, I'm Astra. Tell me your grades, test scores and what you do outside class, and I'll show you where you stand at any school. Type it or say it.",
    canLine: "Before you sign in, I answer from what you tell me here and our data on colleges, majors and summer programs. I can't see or change an account, and I don't search the web.",
    understood: "Here's what I understood. Which school should I check?",
    // While a sanity question is open (picture_checks), the school ask waits.
    understoodClarify: "Here's what I understood. One thing first:",
    schoolPlaceholder: 'Type a school - UCLA, Michigan, MIT...',
    // Once a read is locked (#3480, mock B1): another school's band stays free.
    schoolPlaceholderFree: 'Check another school, free...',
    askPlaceholder: 'Ask Astra about this read',
    // How many free questions a picture gets is an admin setting (#2704):
    // the server sends it as free_questions; 3 until it does.
    // Free questions are a DAY's now (#2725), shared with the open chat.
    counterLeft: (n, total = FREE_QUESTIONS_FALLBACK) => `${n} of ${total} free ${total === 1 ? 'question' : 'questions'} left today`,
    redirectNote: (n, total = FREE_QUESTIONS_FALLBACK) => `That one didn't count. ${n} of ${total} free ${total === 1 ? 'question' : 'questions'} left`,
    limitTitle: 'That was your last free question.',
    limitLine: 'Start free to keep talking with Astra. Your picture and the schools you checked come with you, and she can read your whole record from there.',
    // #2621: "Nothing is saved" was not literal. Since #3063 the questions
    // are kept (scrubbed, 90 days); what you wrote about yourself is not.
    limitSmall: "What you wrote about yourself isn't kept on our side until you sign in. Questions are kept to improve answers.",
    // #2621, #2903, #3063: literally true against the code. The
    // per-conversation record counts kinds of questions and the schools
    // named; who asks exists only as daily totals (never on a record); the
    // questions and answers are kept scrubbed (QUESTIONS_KEPT).
    // AstraNoticeDrift.test.js pins WHO_ASKS to the server's LABEL_KEYS and
    // AUDIENCES.
    notice: `We count the kinds of questions asked and the schools they name, and keep totals of ${WHO_ASKS}. ${QUESTIONS_KEPT}`,
    gpaAsk: "One thing before I read your record - what's your GPA?",
    gpaHint: 'Unweighted (out of 4.0) is best; weighted works too.',
    thinking: 'Astra is reading...',
    building: 'Reading what you wrote...',
    error: 'Something went wrong on our side. Please try again in a moment.',
    // A signed-in account the route refused (founder, 2026-10-01): said, never a silent swap.
    refused: "Astra isn't answering on this page right now. Refresh in a minute and try again.",
};

export const COMPOSER = {
    ariaLabel: "Describe yourself, for example: I'm a junior at Lincoln High in San Jose, California. 3.8 unweighted GPA, 1450 SAT. President of the robotics club. Thinking about mechanical engineering.",
    go: 'Build my picture',
    tryThis: 'Try this one',
    // Under the box while what is typed is under its floor (#5237), so
    // Build my picture is never dead without a reason.
    short: 'Add a little more: your GPA, a test score, or an activity.',
    // The picture you describe is not kept (#2621); questions are (#3063).
    saved: "What you type here isn't kept on our side until you sign in.",
    micStart: 'Speak instead of typing',
    micStop: 'Stop listening',
    listening: 'Listening... tap to stop',
    blocked: 'Mic is blocked. You can type instead, or allow the mic in your browser settings.',
    noMicHint: 'You can also tap the mic on your keyboard.',
    // "Tidy up" (Chrome's on-device Proofreader, only where it is ready)
    tidy: 'Tidy up',
    tidying: 'Tidying...',
    tidyHeading: 'A tidied version',
    tidyUse: 'Use this',
    tidyKeep: 'Keep mine',
    tidyNone: 'Nothing to fix. It reads fine as it is.',
    tidyNote: 'Checked on this device. Nothing left it.',
};

// Words dictation tends to mishear in a student's description, passed to
// the browser as contextual phrase hints where it supports them (Chrome's
// SpeechRecognition.phrases, feature-detected; nothing changes elsewhere).
// The school names are a subset of the knowledge base's own directory: the
// ones a speech model is least likely to spell right.
export const DICTATION_PHRASES = [
    'GPA', 'weighted GPA', 'unweighted GPA', 'weighted', 'unweighted', 'AP', 'AP classes', 'IB', 'IB diploma',
    'SAT', 'ACT', 'PSAT', 'test-optional', 'early decision', 'early action', 'Common App', 'extracurriculars',
    'UCLA', 'UC Berkeley', 'UC San Diego', 'UC Irvine', 'UC Davis', 'UC Santa Barbara', 'UC Santa Cruz',
    'UC Riverside', 'UC Merced', 'Cal Poly', 'Cal Poly San Luis Obispo', 'Cal Poly Pomona', 'Cal State',
    'San Jose State', 'Georgia Tech', 'Caltech', 'MIT', 'USC', 'NYU', 'UIUC', 'UT Austin', 'Carnegie Mellon',
    'Purdue', 'Emory', 'Tufts', 'Vanderbilt', 'Northeastern', 'Rensselaer', 'Worcester Polytechnic',
    'Case Western', 'Johns Hopkins', 'Duke', 'Rice', 'Dartmouth', 'Cornell', 'Brown', 'Yale', 'Princeton',
    'Harvard', 'Stanford', 'Columbia', 'UPenn', 'Northwestern', 'University of Michigan', 'Virginia Tech',
    'Rutgers', 'Barnard', 'Wellesley', 'Pomona College', 'Harvey Mudd', 'Claremont McKenna', 'Santa Clara',
];

// The follow-up chips under the first answer (copy.md, unchanged from A).
export const FOLLOWUP_CHIPS = [
    'Is the SAT required there?',
    'Which schools like this are a better fit?',
    'What should I do this year?',
];

// The first question the page suggests once a school is read, built from
// that school's name and the visitor's own intended major.
export function firstQuestion(read, fields) {
    const school = read?.name || 'this school';
    const major = fields?.intended_major ? ` for ${String(fields.intended_major).toLowerCase()}` : '';
    return `What would help my chances at ${school}${major}?`;
}

// The test policy beside a read's rate line (#3480, mocks B1 and B2: "16.4%
// acceptance rate · test-optional"). Only the server's three values
// (preview_fit._testing_policy); anything else shows nothing.
export const POLICY_WORDS = {
    'test-optional': 'test-optional',
    'test-blind': 'test-blind',
    'test-required': 'test scores required',
};

// The read split at sign-in (#3480; founder pick B of the sign-in strategy
// mocks, 2026-10-08: "the band is free; the reasons need sign-in"). The box's
// title, its list and the band's plain line are the server's, built from the
// read itself (astra_read_unlock.py), so they can only name what is really
// inside. These are the mock's own words around them (mock B1).
export const UNLOCK = {
    // Neutral since #5154: the sheet this opens offers Google, Apple and an
    // email link, so the words name none of them.
    button: 'Sign up free to see it',
    small: (school) => `Your picture and ${school} come with you. We get your name and email from the account you sign in with, nothing else.`,
    // The follow-up chip that needs the reasons opens the box instead of asking
    // (firstQuestion below starts this way).
    chipPrefix: 'What would help my chances at',
};
